// src/components/features/content/ContentReferences.jsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Link as LinkIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Description as DocumentIcon,
  PlayCircle as VideoIcon,
  MoreVert as MoreVertIcon,
  Upload as UploadIcon,
  Photo as GalleryIcon,
} from '@mui/icons-material';

// Import hooks
import useContentStore from '../../../stores/contentStore';
import useNotificationStore from '../../../stores/notificationStore';
import { SecureAsset } from '../../ui/AssetPreview';

/**
 * ContentReferences component for managing references attached to content
 * References can be external links, files, or other pieces of content
 */
const ContentReferences = ({ 
  contentId, 
  references = [], 
  canEdit = false, 
  onRefChange,
  includeFileInData = true, // New prop to ensure file objects are preserved
  onUploadSuccess = null // Add this prop
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeReference, setActiveReference] = useState(null);
  const [referenceDialogOpen, setReferenceDialogOpen] = useState(false);
  const [referenceFormData, setReferenceFormData] = useState({
    type: 'image',
    key: '',
    url: '',
    originalname: '',
    caption: '',
    altText: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  
  // Import store hooks
  const { addContentReference, removeContentReference } = useContentStore();
  const { showSuccess, showError } = useNotificationStore?.() || { 
    showSuccess: (msg) => console.log('Success:', msg),
    showError: (msg) => console.error('Error:', msg)
  };
  
  // Reference types with icons and labels
  const REFERENCE_TYPES = [
    { value: 'image', label: 'Image', icon: <ImageIcon /> },
    { value: 'video', label: 'Video', icon: <VideoIcon /> },
    { value: 'document', label: 'Document', icon: <DocumentIcon /> },
    { value: 'link', label: 'External Link', icon: <LinkIcon /> },
  ];
  
  // Handle menu open for a reference
  const handleMenuOpen = (event, reference) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setActiveReference(reference);
  };
  
  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Open reference dialog to add a new reference
  const handleAddReference = () => {
    setReferenceFormData({
      type: 'image',
      key: '',
      url: '',
      originalname: '',
      caption: '',
      altText: '',
    });
    setFormErrors({});
    setFileToUpload(null);
    setReferenceDialogOpen(true);
  };
  
  // Open reference dialog to edit an existing reference
  const handleEditReference = () => {
    if (!activeReference) return;
    
    setReferenceFormData({
      ...activeReference,
      type: activeReference.type || 'image',
    });
    setFormErrors({});
    setFileToUpload(activeReference.file || null);
    setReferenceDialogOpen(true);
    handleMenuClose();
  };
  
  // Handle reference deletion
  const handleDeleteReference = async () => {
    if (!activeReference) return;
    
    if (!window.confirm('Are you sure you want to remove this reference?')) {
      handleMenuClose();
      return;
    }
    
    try {
      // If using the API
      if (contentId) {
        await removeContentReference(contentId, activeReference._id);
        showSuccess('Reference removed successfully');
      } 
      // For local state management
      else if (onRefChange) {
        const updatedReferences = references.filter(ref => ref._id !== activeReference._id);
        onRefChange(updatedReferences);
      }
    } catch (error) {
      showError('Failed to remove reference: ' + error.message);
    }
    
    handleMenuClose();
  };
  
  // Handle form input changes
  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setReferenceFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setFileToUpload(file);
    
    // Update form data - IMPORTANT: Include the file object
    setReferenceFormData(prev => ({
      ...prev,
      file: file, // Include the actual file object
      originalname: file.name,
      type: file.type.startsWith('image/') 
        ? 'image' 
        : file.type.startsWith('video/') 
          ? 'video' 
          : 'document',
      size: file.size,
      mimetype: file.type
    }));
  };
  
  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (referenceFormData.type === 'link' && !referenceFormData.url) {
      errors.url = 'URL is required for external links';
    } else if (!referenceFormData.url && !fileToUpload && !referenceFormData.key) {
      errors.url = 'Please provide a URL or upload a file';
    }
    
    if (!referenceFormData.caption) {
      errors.caption = 'Caption is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Submit form
  // const handleSubmitReference = async () => {
  //   if (!validateForm()) return;
    
  //   setIsSubmitting(true);
    
  //   try {
  //     // Create complete reference data
  //     let finalData = { ...referenceFormData };
      
  //     // Upload file if present
  //     if (fileToUpload) {
  //       // Set URL for preview
  //       finalData.url = URL.createObjectURL(fileToUpload);
  //       finalData.key = `temp_${Date.now()}`;
        
  //       // Include file object if specified (for proper upload)
  //       if (includeFileInData) {
  //         finalData.file = fileToUpload;
  //       }
  //     }
      
  //     // If editing an existing reference
  //     if (activeReference && activeReference._id) {
  //       finalData._id = activeReference._id;
  //     } else {
  //       // Generate a temporary ID for new references
  //       finalData._id = `temp_${Date.now()}`;
  //     }
      
  //     // If using the API
  //     if (contentId) {
  //       // For API calls, include FormData with file
  //       const formData = new FormData();
        
  //       // Append file if available
  //       if (fileToUpload) {
  //         formData.append('file', fileToUpload);
  //       }
        
  //       // Append metadata
  //       Object.entries(finalData).forEach(([key, value]) => {
  //         if (key !== 'file' && value !== undefined && value !== null) {
  //           formData.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
  //         }
  //       });
        
  //       const response = await addContentReference(contentId, formData);
  //       showSuccess('Reference added successfully');
  //     } 
  //     // For local state management
  //     else if (onRefChange) {
  //       const updatedReferences = activeReference
  //         // Update existing reference
  //         ? references.map(ref => ref._id === activeReference._id ? finalData : ref)
  //         // Add new reference
  //         : [...references, finalData];
          
  //       onRefChange(updatedReferences);
  //     }
      
  //     setReferenceDialogOpen(false);
  //   } catch (error) {
  //     showError('Failed to save reference: ' + (error.message || 'Unknown error'));
  //   } finally {
  //     setIsSubmitting(false);
  //   }
  // };
  // };
// Submit form
const handleSubmitReference = async () => {
  if (!validateForm()) return;
  
  setIsSubmitting(true);
  
  try {
    // Create complete reference data
    let finalData = { ...referenceFormData };
    
    // Upload file if present
    if (fileToUpload) {
      // Set URL for preview
      finalData.url = URL.createObjectURL(fileToUpload);
      finalData.key = `temp_${Date.now()}`;
      
      // Include file object if specified (for proper upload)
      if (includeFileInData) {
        finalData.file = fileToUpload;
      }
    }
    
    // If editing an existing reference
    if (activeReference && activeReference._id) {
      finalData._id = activeReference._id;
    } else {
      // Generate a temporary ID for new references
      finalData._id = `temp_${Date.now()}`;
    }
    
    // If using the API
    if (contentId) {
      try {
        // For API calls, include FormData with file
        const formData = new FormData();
        
        // Append file if available
        if (fileToUpload) {
          formData.append('file', fileToUpload);
        }
        
        // Append metadata
        Object.entries(finalData).forEach(([key, value]) => {
          if (key !== 'file' && value !== undefined && value !== null) {
            formData.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
          }
        });
        
        const response = await addContentReference(contentId, formData);
        showSuccess('Reference added successfully');
        if (onUploadSuccess) {
          onUploadSuccess(response?.data?.reference || response);
        }
        
      } catch (apiError) {
        console.error('API Error:', apiError);
        showError('Failed to save reference: ' + (apiError.message || 'Unknown error'));
        throw apiError; // Re-throw to prevent dialog from closing
      }
    } 
    // For local state management
    else if (onRefChange) {
      const updatedReferences = activeReference
        // Update existing reference
        ? references.map(ref => ref._id === activeReference._id ? finalData : ref)
        // Add new reference
        : [...references, finalData];
        finalData._id = `server_${finalData._id}`;
        onRefChange([...references, finalData]);
      // onRefChange(updatedReferences);
    }
    
    // Always close the dialog when successful
    setReferenceDialogOpen(false);
    // setActiveReference(null); 
  } catch (error) {
    console.error('Reference submission error:', error);
    showError('Failed to save reference: ' + (error.message || 'Unknown error'));
    // Don't close dialog on error
  } finally {
    setIsSubmitting(false);
  }
};
  // Get icon for reference type
  const getReferenceIcon = (type) => {
    switch (type) {
      case 'image': return <ImageIcon />;
      case 'video': return <VideoIcon />;
      case 'document': return <DocumentIcon />;
      case 'link': return <LinkIcon />;
      default: return <FileIcon />;
    }
  };
  
  return (
    <Box>
      {/* References Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2
      }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <LinkIcon sx={{ mr: 1 }} />
          References & Assets
        </Typography>
        
        {canEdit && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddReference}
          >
            Add Reference
          </Button>
        )}
      </Box>
      
      {/* References List */}
      {references.length === 0 ? (
        <Box sx={{ 
          p: 3, 
          bgcolor: alpha(theme.palette.background.paper, 0.5),
          borderRadius: 1,
          border: `1px dashed ${theme.palette.divider}`,
          textAlign: 'center'
        }}>
          <LinkIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1" gutterBottom>
            No references added yet
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Add images, videos, documents, or links that were used as references
          </Typography>
          
          {canEdit && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddReference}
            >
              Add Reference
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={2}>
          {references.map((reference, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card variant="outlined">
             
                {/* Preview */}
                {reference.type === 'image' && reference.url ? (
                     <SecureAsset assetKey={reference} sx={{   height: 140, width:'100%'}}/>
                ) : (
                  <Box sx={{ 
                    height: 140, 
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}>
                    {getReferenceIcon(reference.type)}
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {reference.originalname || reference.url || 'Reference'}
                    </Typography>
                  </Box>
                )}
                
                {/* Content */}
                <CardContent sx={{ pb: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {reference.caption || 'Untitled Reference'}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {reference.altText || reference.originalname || 'No description'}
                  </Typography>
                </CardContent>
                
                {/* Actions */}
                <CardActions sx={{ justifyContent: 'space-between' }}>
                  <Button
                    size="small"
                    startIcon={<ViewIcon />}
                    href={reference.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View
                  </Button>
                  
                  {canEdit && (
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, reference)}>
                      <MoreVertIcon />
                    </IconButton>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Reference Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditReference}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteReference}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography color="error">Remove</Typography>
        </MenuItem>
      </Menu>
      
      {/* Add/Edit Reference Dialog */}
      <Dialog
        open={referenceDialogOpen}
        onClose={() => setReferenceDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {activeReference ? 'Edit Reference' : 'Add Reference'}
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ py: 1 }}>
            {/* Reference Type */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Reference Type</InputLabel>
              <Select
                name="type"
                value={referenceFormData.type}
                onChange={handleFormChange}
                label="Reference Type"
              >
                {REFERENCE_TYPES.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ListItemIcon>{type.icon}</ListItemIcon>
                      {type.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* URL or Upload */}
            {referenceFormData.type === 'link' ? (
              <TextField
                fullWidth
                label="URL"
                name="url"
                value={referenceFormData.url}
                onChange={handleFormChange}
                error={Boolean(formErrors.url)}
                helperText={formErrors.url}
                placeholder="https://example.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  component="label"
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  Upload {referenceFormData.type}
                  <input
                    type="file"
                    hidden
                    onChange={handleFileChange}
                    accept={
                      referenceFormData.type === 'image' 
                        ? 'image/*' 
                        : referenceFormData.type === 'video' 
                          ? 'video/*' 
                          : '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx'
                    }
                  />
                </Button>
                
                {fileToUpload ? (
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Selected file: {fileToUpload.name}
                  </Typography>
                ) : (
                  <TextField
                    fullWidth
                    label="URL (Optional)"
                    name="url"
                    value={referenceFormData.url}
                    onChange={handleFormChange}
                    error={Boolean(formErrors.url)}
                    helperText={formErrors.url || 'Enter a URL or upload a file above'}
                    placeholder={`Enter ${referenceFormData.type} URL`}
                    sx={{ mb: 2 }}
                  />
                )}
              </>
            )}
            
            {/* Caption */}
            <TextField
              fullWidth
              label="Caption"
              name="caption"
              value={referenceFormData.caption}
              onChange={handleFormChange}
              error={Boolean(formErrors.caption)}
              helperText={formErrors.caption}
              placeholder="Enter a caption for this reference"
              sx={{ mb: 2 }}
            />
            
            {/* Alt Text */}
            <TextField
              fullWidth
              label="Alt Text / Description"
              name="altText"
              value={referenceFormData.altText}
              onChange={handleFormChange}
              placeholder="Describe this reference for accessibility"
            />
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => setReferenceDialogOpen(false)} 
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitReference} 
            variant="contained" 
            
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Reference'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContentReferences;